import { type PreferredProductData } from "~/features/productDetails/models/PdpPageProductData";

import { Card, CardActionArea, IconButton, Stack } from "~/common/mui";
import { ShoppingCartOutlined } from "@mui/icons-material";
import { ProductContext } from "~/domains/product/ProductContext";
import type { ProductData } from "~/domains/product/types/ProductData";
import { Product } from "~/domains/product/Product";
import { QuantityStockIndicator } from "~/domains/product/components/StockIndicator/StockIndicator";
import { useState } from "react";
import { AddToCartService } from "~/features/addToCart/AddToCartService";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { canSeeAddToCartButton } from "~/utils/features";
import { usePurchasePlace, usePurchaseType } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { usePriceReferenceSelection } from "~/app/config/useSession";

interface Props {
  product: PreferredProductData;
  showAddToCart: boolean;
}

const VariantA = ({ product }: Pick<Props, "product">) => {
  const [loading, setLoading] = useState(false);
  const { selection, status } = usePriceReferenceSelection();
  const reco_type = usePurchaseType();
  const reco_place = usePurchasePlace();

  const makeOpenAddToCart = (code: string) => async () => {
    if (status !== "ready") {
      return;
    }
    // Fetch product and put into state
    setLoading(true);
    await ProductService.getProductDescriptor(code, getInitialPriceReference(selection), { fetch: true }).promise;
    setLoading(false);
    AddToCartService.openAddToCartWizard(
      {
        productCode: code,
        restrictionQuery: "",
      },
      {
        page: reco_place,
        recommendationType: reco_type,
        recommendationPlace: reco_place,
      },
    );
  };

  const showStock = product.stockAmount !== undefined && userHasPermission(Permission.AVAILABILITY);

  return (
    <ProductContext value={product as ProductData}>
      <Card
        component={Stack}
        direction="row"
        gap={2}
        sx={{ px: 2, py: 1, alignItems: "center", flex: 1, border: "none" }}
      >
        <Product.PdpLink>
          <Product.PrimaryImageV2 size={48} />
        </Product.PdpLink>
        <Stack gap={0.5} flex={1}>
          <Product.DesignationContainer>
            <Product.PreferredIndicator />
            <Product.PdpLink>
              <Product.DesignationV2 fontWeight={"bold"} />
            </Product.PdpLink>
          </Product.DesignationContainer>
          {showStock && <QuantityStockIndicator quantity={product.stockAmount!} />}
        </Stack>
        {canSeeAddToCartButton() && (
          <IconButton loading={loading} sx={{ flex: "none" }} onClick={makeOpenAddToCart(product.code)}>
            <ShoppingCartOutlined color="primary" sx={{ visibility: loading ? "hidden" : "visible" }} />
          </IconButton>
        )}
      </Card>
    </ProductContext>
  );
};

const VariantB = ({ product }: Pick<Props, "product">) => {
  const showStock = product.stockAmount !== undefined && userHasPermission(Permission.AVAILABILITY);

  return (
    <ProductContext value={product as ProductData}>
      <Card sx={{ border: "none" }}>
        <CardActionArea component={Product.PdpLink} sx={{ display: "flex" }}>
          <Stack direction="row" gap={2} sx={{ px: 2, py: 1, alignItems: "center", flex: 1 }}>
            <Product.PrimaryImageV2 size={48} />
            <Stack gap={0.5} flex={1}>
              <Product.DesignationContainer>
                <Product.PreferredIndicator />
                <Product.DesignationV2 fontWeight={"bold"} />
              </Product.DesignationContainer>
              {showStock && <QuantityStockIndicator quantity={product.stockAmount!} />}
            </Stack>
          </Stack>
        </CardActionArea>
      </Card>
    </ProductContext>
  );
};

export const PreferredProductsItem = ({ showAddToCart, product }: Props) => {
  if (showAddToCart) {
    return <VariantA product={product} />;
  }
  return <VariantB product={product} />;
};
